import React, { useEffect, useState, useMemo } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, CircularProgress, Alert, Chip, Divider,
  TextField, InputAdornment,
} from "@mui/material";
import CallMadeIcon from "@mui/icons-material/CallMade";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import StarIcon from "@mui/icons-material/Star";
import SearchIcon from "@mui/icons-material/Search";
import StoreIcon from "@mui/icons-material/Store";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS, buildDialpadPath } from "@/config/apiConstants";

const DIALPAD_PINK = "#E63888";

function fmtDuration(seconds) {
  if (!seconds) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function fmtTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-AU", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });
}

// ── Call picker row (used in card-context mode) ──────────────────────────────
function CallRow({ call, onSelect, selected }) {
  const isSuggestion = call.is_suggestion;
  return (
    <Box
      onClick={() => onSelect(call)}
      sx={{
        display: "flex", alignItems: "center", gap: 1.5,
        p: 1.5, borderRadius: 1.5, mb: 0.75,
        border: selected ? `2px solid ${DIALPAD_PINK}` : "1px solid #e5e7eb",
        backgroundColor: selected ? "#FDE8F3" : isSuggestion ? "#fff5f9" : "#fff",
        cursor: "pointer",
        "&:hover": { borderColor: DIALPAD_PINK, backgroundColor: "#FDE8F3" },
        transition: "all 0.12s",
      }}
    >
      <Box sx={{ color: call.direction === "inbound" ? "#059669" : "#2563eb", flexShrink: 0 }}>
        {call.direction === "inbound"
          ? <CallReceivedIcon fontSize="small" />
          : <CallMadeIcon fontSize="small" />}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {call.external_number || "Unknown"}
          </Typography>
          {isSuggestion && (
            <Chip
              icon={<StarIcon sx={{ fontSize: "12px !important" }} />}
              label="Matches"
              size="small"
              sx={{ height: 18, fontSize: "0.6rem", backgroundColor: "#FDE8F3", color: DIALPAD_PINK }}
            />
          )}
        </Box>
        <Typography variant="caption" color="text.secondary">
          {fmtTime(call.start_time)} · {fmtDuration(call.duration_seconds)} · {call.manager_name || "Unknown"}
        </Typography>
      </Box>
      {selected && (
        <Chip label="Selected" size="small" sx={{ backgroundColor: DIALPAD_PINK, color: "#fff", fontWeight: 600 }} />
      )}
    </Box>
  );
}

// ── Chemist picker row (used in unallocated-calls mode) ──────────────────────
function ChemistRow({ chemist, selected, onSelect }) {
  return (
    <Box
      onClick={() => onSelect(chemist)}
      sx={{
        display: "flex", alignItems: "center", gap: 1.5,
        p: 1.25, borderRadius: 1.5, mb: 0.5,
        border: selected ? `2px solid ${DIALPAD_PINK}` : "1px solid #e5e7eb",
        backgroundColor: selected ? "#FDE8F3" : "#fff",
        cursor: "pointer",
        "&:hover": { borderColor: DIALPAD_PINK, backgroundColor: "#FDE8F3" },
        transition: "all 0.12s",
      }}
    >
      <Box sx={{ color: DIALPAD_PINK, flexShrink: 0 }}>
        <StoreIcon fontSize="small" />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
          {chemist.chemistName}
        </Typography>
        {chemist.suburb && (
          <Typography variant="caption" color="text.secondary">{chemist.suburb}</Typography>
        )}
      </Box>
      {selected && (
        <Chip label="Selected" size="small" sx={{ backgroundColor: DIALPAD_PINK, color: "#fff", fontWeight: 600 }} />
      )}
    </Box>
  );
}

// ── Main modal ────────────────────────────────────────────────────────────────
/**
 * Three modes:
 *   1. Card-context mode    — pass `cardId` + `context`
 *      Shows recent unallocated calls to link to the card.
 *
 *   2. Chemist-picker mode  — pass `call`
 *      Shows a searchable chemist list to allocate the specific call to.
 *
 *   3. Chemist-context mode — pass `chemistId` + `chemistName`
 *      Shows recent unallocated calls (highlighting phone matches) to allocate to a specific chemist.
 */
const AllocateCallModal = ({ open, onClose, context, cardId, call, chemistId, chemistName, onAllocated }) => {
  const isChemistMode   = !!call && !cardId;           // mode 2
  const isChemistCtxMode = !!chemistId && !call && !cardId; // mode 3

  // ── Card-context state ──
  const [calls, setCalls] = useState([]);
  const [selectedCall, setSelectedCall] = useState(null);

  // ── Chemist-picker state ──
  const [chemists, setChemists] = useState([]);
  const [chemistSearch, setChemistSearch] = useState("");
  const [selectedChemist, setSelectedChemist] = useState(null);

  // ── Auto-match state (mode 2 only) ──
  const [autoMatch, setAutoMatch] = useState(null);     // { chemistID, chemistName, chemistUUID }
  const [autoMatchLoading, setAutoMatchLoading] = useState(false);
  const [showManualSearch, setShowManualSearch] = useState(false);

  // ── Shared state ──
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // ── Step-2: save phone ──
  const [step, setStep] = useState(1); // 1 = allocate, 2 = save phone
  const [contactName, setContactName] = useState("");
  const [savingPhone, setSavingPhone] = useState(false);
  const [phoneSaved, setPhoneSaved] = useState(false);

  // Load data on open
  useEffect(() => {
    if (!open) return;
    setError(null);
    setSelectedCall(null);
    setSelectedChemist(null);
    setChemistSearch("");
    setStep(1);
    setContactName("");
    setSavingPhone(false);
    setPhoneSaved(false);
    setAutoMatch(null);
    setShowManualSearch(false);

    if (isChemistMode) {
      // Mode 2: first try auto-match, then load full chemist list in parallel
      if (call?.external_number) {
        setAutoMatchLoading(true);
        phappsRequest(`${API_PATHS.DIALPAD_MATCH_PHONE}?phone=${encodeURIComponent(call.external_number)}`, "GET")
          .then(data => { if (data?.match) setAutoMatch(data.match); })
          .catch(() => {}) // non-fatal
          .finally(() => setAutoMatchLoading(false));
      }
      setLoading(true);
      phappsRequest(API_PATHS.CHEMIST_LIST, "GET")
        .then(data => setChemists(data?.chemists ?? []))
        .catch(() => setError("Failed to load chemist list"))
        .finally(() => setLoading(false));
    } else if (isChemistCtxMode) {
      // Mode 3: load recent calls annotated against this chemist's phone
      setLoading(true);
      phappsRequest(`${API_PATHS.DIALPAD_CALLS_RECENT}?context=chemist&id=${chemistId}`, "GET")
        .then(data => setCalls(data?.calls ?? []))
        .catch(() => setError("Failed to load recent calls"))
        .finally(() => setLoading(false));
    } else {
      // Mode 1: load recent calls for this card
      if (!cardId) return;
      setLoading(true);
      phappsRequest(`${API_PATHS.DIALPAD_CALLS_RECENT}?context=${context}&id=${cardId}`, "GET")
        .then(data => setCalls(data?.calls ?? []))
        .catch(() => setError("Failed to load recent calls"))
        .finally(() => setLoading(false));
    }
  }, [open, isChemistMode, isChemistCtxMode, cardId, context, chemistId, call?.external_number]);

  // Filtered chemist list
  const filteredChemists = useMemo(() => {
    const q = chemistSearch.trim().toLowerCase();
    if (!q) return chemists;
    return chemists.filter(c => (c.chemistName || "").toLowerCase().includes(q));
  }, [chemists, chemistSearch]);

  const suggestions = calls.filter(c => c.is_suggestion);
  const others = calls.filter(c => !c.is_suggestion);

  const handleAllocate = async () => {
    setSaving(true);
    setError(null);
    try {
      if (isChemistMode) {
        // Mode 2: use auto-matched chemist if confirmed, otherwise use selected from search
        const chemistToUse = autoMatch && !showManualSearch ? autoMatch : selectedChemist;
        await phappsRequest(
          buildDialpadPath(`calls/${call.id}/allocate`),
          "POST",
          { chemist_id: chemistToUse.chemistID }
        );
        onAllocated?.();
        if (call.external_number && (chemistToUse.PHUUID ?? chemistToUse.chemistUUID)) {
          setStep(2);
          // For step 2 save-phone, set selectedChemist so handleSavePhone works
          if (!selectedChemist) setSelectedChemist({ ...chemistToUse, PHUUID: chemistToUse.chemistUUID });
        } else {
          onClose();
        }
      } else if (isChemistCtxMode) {
        // Mode 3: allocate selected call to this chemist
        await phappsRequest(
          buildDialpadPath(`calls/${selectedCall.id}/allocate`),
          "POST",
          { chemist_id: chemistId }
        );
        onAllocated?.();
        onClose();
      } else {
        // Mode 1: allocate selected call to card
        await phappsRequest(
          buildDialpadPath(`calls/${selectedCall.id}/allocate`),
          "POST",
          { card_id: cardId }
        );
        onAllocated?.();
        onClose();
      }
    } catch {
      setError("Failed to allocate call. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleSavePhone = async () => {
    setSavingPhone(true);
    setError(null);
    try {
      await phappsRequest(API_PATHS.ADMIN_CHEMIST_NUMBERS, "POST", {
        ChemistUUID: selectedChemist.PHUUID,
        phone: call.external_number,
        contact_name: contactName.trim() || null,
        position: null,
      });
      setPhoneSaved(true);
      setTimeout(() => onClose(), 1000);
    } catch {
      setError("Failed to save phone number. You can try again from the chemist page.");
      setSavingPhone(false);
    }
  };

  const hasAutoMatch = isChemistMode && !!autoMatch && !showManualSearch;
  const canConfirm = hasAutoMatch || (isChemistMode ? !!selectedChemist : !!selectedCall);

  const modalTitle = step === 2
    ? "Save phone number?"
    : isChemistMode
      ? "Allocate Call to Chemist"
      : isChemistCtxMode
        ? `Allocate call to ${chemistName || "Chemist"}`
        : "Allocate Call";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ fontWeight: 700, color: DIALPAD_PINK, borderBottom: "1px solid #e5e7eb", pb: 1.5 }}>
        {modalTitle}
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {/* ── Step 2: Save phone prompt ── */}
        {step === 2 && (
          <>
            {phoneSaved ? (
              <Box sx={{ py: 2, textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", gap: 1, color: "#059669" }}>
                <CheckCircleOutlineIcon />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Number saved successfully.</Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ mb: 2, p: 1.25, borderRadius: 1.5, backgroundColor: "#f9fafb", border: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: 1.5 }}>
                  <PhoneAndroidIcon sx={{ color: DIALPAD_PINK, fontSize: 20, flexShrink: 0 }} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{call?.external_number}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Will be added to <strong>{selectedChemist?.chemistName}</strong>'s contact numbers for future auto-matching
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                  Contact name <em>(optional)</em>
                </Typography>
                <TextField
                  fullWidth size="small"
                  placeholder="Enter the name"
                  value={contactName}
                  onChange={e => setContactName(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && !savingPhone && handleSavePhone()}
                  autoFocus
                />
                {error && <Alert severity="warning" sx={{ mt: 1.5, py: 0.5 }}>{error}</Alert>}
              </>
            )}
          </>
        )}

        {step === 1 && error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

        {/* ── Step 1 content ── */}
        {/* ── Call summary (chemist mode only) ── */}
        {step === 1 && isChemistMode && call && (
          <Box sx={{ mb: 2, p: 1.25, borderRadius: 1.5, backgroundColor: "#f9fafb", border: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ color: call.direction === "inbound" ? "#059669" : "#2563eb", flexShrink: 0 }}>
              {call.direction === "inbound" ? <CallReceivedIcon fontSize="small" /> : <CallMadeIcon fontSize="small" />}
            </Box>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{call.external_number || "Unknown number"}</Typography>
              <Typography variant="caption" color="text.secondary">
                {fmtTime(call.start_time)} · {fmtDuration(call.duration_seconds)} · {call.manager_name || "Unknown"}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Single loading gate — wait for both lookup + chemist list before showing anything */}
        {step === 1 && (loading || autoMatchLoading) ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} sx={{ color: DIALPAD_PINK }} />
          </Box>
        ) : step === 1 && isChemistMode && hasAutoMatch && !showManualSearch ? (
          /* ── Auto-match: one-click confirm ── */
          <Box sx={{ p: 2, borderRadius: 2, border: `2px solid ${DIALPAD_PINK}`, bgcolor: "#FDE8F3" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <AutoAwesomeIcon sx={{ color: DIALPAD_PINK, fontSize: 18 }} />
              <Typography variant="body2" sx={{ fontWeight: 700, color: DIALPAD_PINK }}>
                Phone number matched
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1.25, borderRadius: 1.5, bgcolor: "#fff", border: "1px solid #f9a8d4", mb: 1.5 }}>
              <StoreIcon sx={{ fontSize: 16, color: DIALPAD_PINK, flexShrink: 0 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{autoMatch.chemistName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {call?.external_number} is linked to this pharmacy
                </Typography>
              </Box>
            </Box>
            <Button
              size="small"
              variant="text"
              onClick={() => setShowManualSearch(true)}
              sx={{ textTransform: "none", fontSize: "0.72rem", color: "#9ca3af", p: 0 }}
            >
              Not the right pharmacy? Search manually →
            </Button>
          </Box>
        ) : step === 1 && isChemistMode && (!hasAutoMatch || showManualSearch) ? (
          /* ── Chemist picker (manual search) ── */
          <>
            {showManualSearch && (
              <Button size="small" onClick={() => setShowManualSearch(false)}
                sx={{ textTransform: "none", fontSize: "0.72rem", color: DIALPAD_PINK, mb: 1, p: 0 }}>
                ← Back to matched chemist
              </Button>
            )}
            <TextField
              fullWidth size="small"
              placeholder="Search chemist name…"
              value={chemistSearch}
              onChange={e => setChemistSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              sx={{ mb: 1.5 }}
            />
            <Box sx={{ maxHeight: 340, overflowY: "auto" }}>
              {filteredChemists.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                  {chemistSearch ? "No chemists match your search." : "No chemists found."}
                </Typography>
              ) : (
                filteredChemists.map(c => (
                  <ChemistRow
                    key={c.chemistID}
                    chemist={c}
                    selected={selectedChemist?.chemistID === c.chemistID}
                    onSelect={setSelectedChemist}
                  />
                ))
              )}
            </Box>
          </>
        ) : (
          /* ── Call picker (card-context mode) ── */
          calls.length === 0 ? null : (
            <>
              {suggestions.length > 0 && (
                <>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: DIALPAD_PINK, textTransform: "uppercase", letterSpacing: 0.5 }}>
                    Suggested matches
                  </Typography>
                  <Box sx={{ mt: 0.75, mb: 1.5 }}>
                    {suggestions.map(c => (
                      <CallRow key={c.id} call={c} selected={selectedCall?.id === c.id} onSelect={setSelectedCall} />
                    ))}
                  </Box>
                  {others.length > 0 && <Divider sx={{ mb: 1.5 }} />}
                </>
              )}
              {others.length > 0 && (
                <>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5 }}>
                    Other unallocated calls
                  </Typography>
                  <Box sx={{ mt: 0.75 }}>
                    {others.map(c => (
                      <CallRow key={c.id} call={c} selected={selectedCall?.id === c.id} onSelect={setSelectedCall} />
                    ))}
                  </Box>
                </>
              )}
            </>
          )
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {step === 2 && !phoneSaved ? (
          <>
            <Button onClick={onClose} sx={{ textTransform: "none" }}>Skip</Button>
            <Button
              variant="contained"
              disabled={savingPhone}
              onClick={handleSavePhone}
              startIcon={savingPhone ? <CircularProgress size={13} sx={{ color: "#fff" }} /> : null}
              sx={{ textTransform: "none", fontWeight: 600, backgroundColor: DIALPAD_PINK, "&:hover": { backgroundColor: "#c42d72" } }}
            >
              {savingPhone ? "Saving…" : "Save number"}
            </Button>
          </>
        ) : step === 1 ? (
          <>
            <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
            <Button
              variant="contained"
              disabled={!canConfirm || saving}
              onClick={handleAllocate}
              sx={{ textTransform: "none", fontWeight: 600, backgroundColor: DIALPAD_PINK, "&:hover": { backgroundColor: "#c42d72" } }}
            >
              {saving ? "Allocating…" : "Allocate"}
            </Button>
          </>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

export default AllocateCallModal;
